import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence, useAnimation } from 'framer-motion';
import { Section } from '../shared';

// Dynamic Waveform Graph Component (SVG manipulation)
const DynamicWaveform = ({ value, isActive }) => {
  const [points, setPoints] = useState([]);
  const svgRef = useRef(null);
  
  useEffect(() => {
    const generateWaveform = () => {
      const newPoints = [];
      const numPoints = 50;
      const amplitude = 20 + (value / 100) * 30;
      const frequency = 0.5 + (value / 100) * 1.5;
      
      for (let i = 0; i < numPoints; i++) {
        const x = (i / (numPoints - 1)) * 100;
        const noise = isActive ? Math.random() * 5 : 0;
        const y = 50 + Math.sin((i * frequency) + Date.now() * 0.003) * amplitude + noise;
        newPoints.push({ x, y });
      }
      setPoints(newPoints);
    };

    if (isActive) {
      const interval = setInterval(generateWaveform, 50);
      return () => clearInterval(interval);
    } else {
      generateWaveform();
    }
  }, [value, isActive]);

  const pathD = points.length > 0
    ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
    : '';

  return (
    <svg 
      ref={svgRef}
      className="w-full h-24 rounded-lg bg-dark-base/50 overflow-hidden"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FD802E" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#FD802E" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#FD802E" stopOpacity="0.2" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Background grid lines */}
      {[25, 50, 75].map(y => (
        <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#FD802E" strokeOpacity="0.1" strokeWidth="0.3" />
      ))}
      
      {/* Waveform path */}
      <motion.path
        d={pathD}
        fill="none"
        stroke="url(#waveGradient)"
        strokeWidth="2"
        filter="url(#glow)"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5 }}
      />
      
      {/* Reflection */}
      <motion.path
        d={pathD}
        fill="none"
        stroke="#FD802E"
        strokeWidth="1"
        strokeOpacity="0.2"
        transform="translate(0, 10)"
      />
    </svg>
  );
};

// Interactive Slider Component
const FeatureSlider = ({ label, value, onChange, unit = '%' }) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">{label}</span>
        <span className="text-neon-orange font-mono">{value}{unit}</span>
      </div>
      <div className="relative h-2 bg-dark-base rounded-full overflow-hidden">
        <motion.div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-neon-orange/50 to-neon-orange rounded-full"
          style={{ width: `${value}%` }}
          layoutId="sliderFill"
        />
        <input
          type="range"
          min="0"
          max="100"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-neon-orange rounded-full shadow-[0_0_10px_rgba(253,128,46,0.6)] cursor-pointer"
          style={{ left: `calc(${value}% - 8px)` }}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
        />
      </div>
    </div>
  );
};

// Pulsing Icon Component
const PulsingIcon = ({ icon, isHovered }) => {
  const icons = {
    ai: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    voice: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
    music: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>
    ),
    lightning: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    workflow: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    analytics: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  };
  const isImage = typeof icon === 'string' && /\.(webp|png|jpg|jpeg|svg)$/.test(icon.toLowerCase());
  const content = isImage ? (
    <img src={icon} alt="feature" className="w-10 h-10 object-contain" />
  ) : (icons[icon] || null);

  return (
    <motion.div
      className="relative w-16 h-16 flex items-center justify-center"
      animate={isHovered ? {
        scale: [1, 1.1, 1, 1.1, 1],
      } : {}}
      transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
    >
      {/* Pulsing rings */}
      {isHovered && (
        <>
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-neon-orange"
            animate={{ scale: [1, 1.5, 1.5], opacity: [0.8, 0, 0] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-neon-orange"
            animate={{ scale: [1, 1.3, 1.3], opacity: [0.6, 0, 0] }}
            transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
          />
        </>
      )}
      
      <motion.div
        className="w-16 h-16 bg-neon-orange/10 rounded-2xl flex items-center justify-center text-neon-orange relative z-10"
        animate={{ 
          backgroundColor: isHovered ? 'rgba(253, 128, 46, 0.3)' : 'rgba(253, 128, 46, 0.1)',
          boxShadow: isHovered 
            ? '0 0 30px rgba(253, 128, 46, 0.5)' 
            : '0 0 0px rgba(253, 128, 46, 0)'
        }}
        transition={{ duration: 0.3 }}
      >
        {content}
      </motion.div>
    </motion.div>
  );
};

// Wavy Card SVG Border
const WavyCardBorder = ({ isHovered }) => (
  <svg 
    className="absolute inset-0 w-full h-full pointer-events-none" 
    preserveAspectRatio="none"
    viewBox="0 0 400 300"
  >
    <defs>
      <filter id="neonGlow">
        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <motion.path
      d="M 20,0 
         Q 0,0 0,20 
         L 0,250 
         Q 0,260 5,265 
         C 50,280 100,290 200,285 
         C 300,280 350,275 395,265 
         Q 400,260 400,250 
         L 400,20 
         Q 400,0 380,0 
         Z"
      fill="none"
      stroke="#FD802E"
      strokeWidth={isHovered ? "2" : "1"}
      strokeOpacity={isHovered ? 0.8 : 0.3}
      filter={isHovered ? "url(#neonGlow)" : "none"}
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.5, ease: "easeInOut" }}
    />
  </svg>
);

// Feature Card with Wavy Shape
const WavyFeatureCard = ({ feature, index, totalCards }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [sliderValue, setSliderValue] = useState(50);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  // Calculate overlap offset for desktop wave effect
  const overlapOffset = index % 2 === 0 ? 0 : 40;
  const horizontalWave = Math.sin(index * 0.8) * 20;

  return (
    <motion.div
      ref={ref}
      className="relative"
      style={{ 
        zIndex: totalCards - index,
      }}
      initial={{ opacity: 0, y: 50, x: horizontalWave }}
      animate={isInView ? { 
        opacity: 1, 
        y: 0, 
        x: horizontalWave 
      } : {}}
      transition={{ 
        delay: index * 0.15,
        duration: 0.6,
        ease: "easeOut"
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.div
        className="relative bg-dark-deeper/90 backdrop-blur-sm rounded-3xl overflow-hidden min-h-[320px]"
        style={{ marginTop: overlapOffset }}
        animate={{
          y: isHovered ? -10 : 0,
          boxShadow: isHovered 
            ? '0 20px 60px rgba(253, 128, 46, 0.2)' 
            : '0 10px 30px rgba(0, 0, 0, 0.3)'
        }}
        transition={{ duration: 0.3 }}
      >
        {/* Wavy SVG Border */}
        <WavyCardBorder isHovered={isHovered} />
        
        {/* Subtle wave animation background */}
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(ellipse at ${50 + Math.sin(index) * 30}% ${30 + Math.cos(index) * 20}%, rgba(253, 128, 46, 0.3) 0%, transparent 60%)`
          }}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{ duration: 3 + index * 0.5, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative z-10 p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <PulsingIcon icon={feature.icon} isHovered={isHovered} />
          </div>
          
          {/* Title */}
          <motion.h3 
            className="text-xl font-heading font-bold text-white text-center mb-3"
            animate={{ color: isHovered ? '#FD802E' : '#ffffff' }}
            transition={{ duration: 0.3 }}
          >
            {feature.title}
          </motion.h3>
          
          {/* Description */}
          <p className="text-gray-400 text-sm text-center mb-4">
            {feature.description}
          </p>
          
          {/* Interactive Demo Section */}
          <AnimatePresence>
            {showDemo && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4 mb-4"
              >
                <FeatureSlider
                  label={feature.sliderLabel || "Intensity"}
                  value={sliderValue}
                  onChange={setSliderValue}
                  unit={feature.sliderUnit || "%"}
                />
                <DynamicWaveform value={sliderValue} isActive={showDemo} />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Try It Button */}
          <motion.button
            onClick={() => setShowDemo(!showDemo)}
            className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
            style={{
              background: showDemo 
                ? 'linear-gradient(135deg, rgba(253,128,46,0.3) 0%, rgba(253,128,46,0.1) 100%)'
                : 'linear-gradient(135deg, rgba(253,128,46,0.15) 0%, rgba(253,128,46,0.05) 100%)',
              border: '1px solid rgba(253,128,46,0.3)',
              color: '#FD802E'
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: '0 0 20px rgba(253, 128, 46, 0.3)'
            }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.span
              animate={showDemo ? { rotate: 180 } : { rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showDemo ? "M6 18L18 6M6 6l12 12" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"} />
                {!showDemo && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
              </svg>
            </motion.span>
            {showDemo ? 'Close Demo' : 'Try It'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Mobile Swipeable Card
const MobileSwipeableCard = ({ feature, index, onSwipe, isActive }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [sliderValue, setSliderValue] = useState(50);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > minSwipeDistance;
    const isDownSwipe = distance < -minSwipeDistance;
    
    if (isUpSwipe) onSwipe('up');
    if (isDownSwipe) onSwipe('down');
  };

  return (
    <motion.div
      className="relative bg-dark-deeper/90 backdrop-blur-sm rounded-3xl overflow-hidden mx-4"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: isActive ? 1 : 0.5, 
        scale: isActive ? 1 : 0.9,
        y: isActive ? 0 : 20
      }}
      transition={{ duration: 0.4 }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={() => setIsHovered(!isHovered)}
    >
      <WavyCardBorder isHovered={isActive} />
      
      <div className="relative z-10 p-6">
        <div className="flex justify-center mb-4">
          <PulsingIcon icon={feature.icon} isHovered={isActive} />
        </div>
        
        <h3 className="text-xl font-heading font-bold text-white text-center mb-3">
          {feature.title}
        </h3>
        
        <p className="text-gray-400 text-sm text-center mb-4">
          {feature.description}
        </p>
        
        <AnimatePresence>
          {showDemo && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 mb-4"
            >
              <FeatureSlider
                label={feature.sliderLabel || "Intensity"}
                value={sliderValue}
                onChange={setSliderValue}
              />
              <DynamicWaveform value={sliderValue} isActive={showDemo} />
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.button
          onClick={(e) => { e.stopPropagation(); setShowDemo(!showDemo); }}
          className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-neon-orange/10 border border-neon-orange/30 text-neon-orange"
          whileTap={{ scale: 0.98 }}
        >
          {showDemo ? 'Close Demo' : 'Try It'}
        </motion.button>
      </div>
    </motion.div>
  );
};

// Mobile Vertical Carousel
const MobileCarousel = ({ features }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  const handleSwipe = (direction) => {
    if (direction === 'up' && activeIndex < features.length - 1) {
      setActiveIndex(prev => prev + 1);
    } else if (direction === 'down' && activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Carousel indicators */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20">
        {features.map((_, i) => (
          <motion.button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`w-2 h-8 rounded-full transition-all ${
              i === activeIndex ? 'bg-neon-orange' : 'bg-neon-orange/30'
            }`}
            whileHover={{ scale: 1.2 }}
            animate={{
              height: i === activeIndex ? 32 : 16,
              boxShadow: i === activeIndex ? '0 0 10px rgba(253, 128, 46, 0.5)' : 'none'
            }}
          />
        ))}
      </div>

      {/* Cards container */}
      <div className="overflow-hidden py-4">
        <motion.div
          animate={{ y: -activeIndex * 380 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="space-y-6"
        >
          {features.map((feature, i) => (
            <div key={i} className="h-[360px]">
              <MobileSwipeableCard
                feature={feature}
                index={i}
                isActive={i === activeIndex}
                onSwipe={handleSwipe}
              />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Swipe hint */}
      <motion.div 
        className="text-center text-gray-500 text-sm mt-4"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span>Swipe up/down or tap indicators</span>
      </motion.div>
    </div>
  );
};

// Equalizer Header Animation
const EqualizerHeader = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [bars, setBars] = useState(Array(12).fill(20));

  useEffect(() => {
    if (isInView) {
      const interval = setInterval(() => {
        setBars(prev => prev.map(() => 20 + Math.random() * 60));
      }, 150);
      
      // Stop after animation plays
      setTimeout(() => {
        clearInterval(interval);
        setBars(Array(12).fill(40));
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [isInView]);

  return (
    <div ref={ref} className="text-center mb-16">
      {/* Equalizer bars behind title */}
      <div className="relative inline-block">
        <div className="absolute -inset-x-8 -top-4 flex items-end justify-center gap-1 h-16 opacity-30">
          {bars.map((height, i) => (
            <motion.div
              key={i}
              className="w-2 bg-gradient-to-t from-neon-orange/50 to-neon-orange rounded-full"
              initial={{ height: 4 }}
              animate={{ height: isInView ? height : 4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            />
          ))}
        </div>
        
        <motion.span 
          className="relative z-10 text-neon-orange font-semibold text-sm uppercase tracking-wider inline-block"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          Key Features
        </motion.span>
      </div>
      
      <motion.h2 
        className="text-3xl md:text-5xl font-heading font-bold text-white mt-6 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.1 }}
      >
        Advanced Tools for{' '}
        <motion.span
          className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light inline-block relative"
          initial={{ backgroundSize: '100% 100%' }}
          animate={isInView ? {
            textShadow: [
              '0 0 20px rgba(253, 128, 46, 0.3)',
              '0 0 50px rgba(253, 128, 46, 0.8)',
              '0 0 20px rgba(253, 128, 46, 0.3)',
            ]
          } : {}}
          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
        >
          Audio Automation
          {/* Expanding underline like equalizer */}
          <motion.div
            className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-orange to-transparent"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.5, duration: 0.8 }}
          />
        </motion.span>
      </motion.h2>
      
      <motion.p 
        className="text-gray-400 max-w-2xl mx-auto text-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.2 }}
      >
        Everything you need to create, edit, and optimize audio content at scale.
      </motion.p>
    </div>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: "/Key%20Features.webp",
      title: "AI Voice Enhancement",
      description: "Crystal-clear audio with intelligent noise reduction and voice optimization algorithms that adapt to your content.",
      sliderLabel: "Clarity",
      sliderUnit: "%"
    },
    {
      icon: "/Key%20Features%20(2).webp",
      title: "Smart Music Generation",
      description: "Generate royalty-free music and sound effects tailored to your project needs.",
      sliderLabel: "Tempo",
      sliderUnit: " BPM"
    },
    {
      icon: "/Key%20Features%20(3).webp",
      title: "Real-time Processing",
      description: "Lightning-fast audio processing with live preview and instant adjustments.",
      sliderLabel: "Speed",
      sliderUnit: "x"
    },
    {
      icon: "/Key%20Features%20(4).webp",
      title: "Workflow Automation",
      description: "Automate repetitive tasks with custom workflows and batch processing capabilities.",
      sliderLabel: "Efficiency",
      sliderUnit: "%"
    },
    {
      icon: "/Key%20Features%20(5).webp",
      title: "Audio Analytics",
      description: "Deep insights into your audio with waveform analysis and quality metrics.",
      sliderLabel: "Detail",
      sliderUnit: "%"
    },
    {
      icon: "/Key%20Features%20(6).webp",
      title: "Neural Audio Mixing",
      description: "AI-powered mixing and mastering that adapts to your style and preferences.",
      sliderLabel: "Volume",
      sliderUnit: " dB"
    },
  ];

  return (
    <Section id="features" className="bg-dark-base relative overflow-hidden">
      {/* Background wave patterns */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Animated wave SVGs */}
        <svg className="absolute top-0 left-0 w-full h-64 opacity-5" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <motion.path
            fill="#FD802E"
            d="M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,186.7C960,213,1056,235,1152,224C1248,213,1344,171,1392,149.3L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
            animate={{
              d: [
                "M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,186.7C960,213,1056,235,1152,224C1248,213,1344,171,1392,149.3L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z",
                "M0,128L48,149.3C96,171,192,213,288,224C384,235,480,213,576,186.7C672,160,768,128,864,138.7C960,149,1056,203,1152,213.3C1248,224,1344,192,1392,176L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
        </svg>
        
        <motion.div
          className="absolute top-1/4 -left-32 w-64 h-64 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <EqualizerHeader />

        {/* Desktop: Staggered Overlapping Wave Layout */}
        <div className="hidden md:block">
          <div className="grid grid-cols-3 gap-6 relative">
            {features.map((feature, i) => (
              <WavyFeatureCard 
                key={i} 
                feature={feature} 
                index={i}
                totalCards={features.length}
              />
            ))}
          </div>
        </div>

        {/* Mobile: Vertical Swipeable Carousel */}
        <div className="md:hidden">
          <MobileCarousel features={features} />
        </div>
      </div>
    </Section>
  );
};

export default FeaturesSection;
